/**
 * What a screen says instead of the plan, while it is read and when it cannot
 * be.
 *
 * A refusal is said in one sentence about what happened and what to do, for a
 * dispatcher. The API's own `detail`, and any message from the browser or
 * from MSAL, is written for whoever runs the planner and is never shown (UI
 * review, 2 October 2026): it stays in the network log and the console. The
 * sentence branches on the envelope's `code`, which is why `api/client.ts`
 * keeps it.
 */
import { ApiError } from "@depicomnlbv/platform-client";
import { Button, EmptyState } from "@depicomnlbv/platform-ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { errorCode, PlannerApiError, SignInExpired, signInAgain } from "../api";
import { formatDate } from "../time";

/** What the refusal was about, where the screen knows. */
export interface FailureContext {
  runId?: number;
  date?: string;
  /** The Monday that names a week. */
  monday?: string;
  /** A screen whose roles are not the dispatcher's (Milk Flow), named in the refusal. */
  screen?: string;
}

interface Explained {
  sentence: string;
  /** Whether the run list is the way on. */
  toRuns: boolean;
  /** Whether a "Sign in again" button is the way on. */
  signIn?: boolean;
}

export function explain(error: unknown, context: FailureContext = {}): Explained {
  if (error instanceof SignInExpired) {
    // MSAL could not renew the sign-in in the background: the request never
    // left the browser, so the planner is not to blame (Rick, 2 October 2026).
    return error.signingIn
      ? { sentence: "Your sign-in has expired. Taking you to the sign-in page.", toRuns: false }
      : { sentence: "Your sign-in has expired.", toRuns: false, signIn: true };
  }
  const code = errorCode(error);
  if ((code === "USER_NOT_PROVISIONED" || code === "ROLE_NOT_PERMITTED") && context.screen) {
    return {
      sentence:
        `You are signed in, but your account does not have a role that opens ${context.screen}. ` +
        "Ask the administrator to give you access.",
      toRuns: false,
    };
  }
  if (code === "USER_NOT_PROVISIONED" || code === "ROLE_NOT_PERMITTED") {
    return {
      sentence:
        "You are signed in, but your account does not have the dispatcher role the planner needs. " +
        "Ask the administrator to give you access.",
      toRuns: false,
    };
  }
  if (code === "RUN_NOT_FOUND") {
    return {
      sentence:
        context.runId !== undefined
          ? `There is no run ${context.runId}. Pick a run from the list.`
          : "That run does not exist. Pick a run from the list.",
      toRuns: true,
    };
  }
  if (code === "DAY_OUTSIDE_HORIZON") {
    const what = context.date ? formatDate(context.date) : "That day";
    const run = context.runId !== undefined ? `run ${context.runId}` : "this run";
    return {
      sentence: `${what} is outside the planning horizon of ${run}. Pick a day inside it, or another run.`,
      toRuns: true,
    };
  }
  if (code === "WEEK_OUTSIDE_HORIZON") {
    const what = context.monday ? `The week of ${formatDate(context.monday)}` : "That week";
    const run = context.runId !== undefined ? `run ${context.runId}` : "this run";
    return {
      sentence: `${what} is outside the planning horizon of ${run}. Pick a week inside it, or another run.`,
      toRuns: true,
    };
  }
  if (error instanceof ApiError && error.isUnauthenticated) {
    // Two different 401s. One is a sign-in that is missing, and the client has
    // already started the redirect that supplies it. The other is the API
    // refusing a sign-in that happened (or a second refusal after the one
    // redirect a tab session gets): another redirect would bring back the
    // same token, so the reader is told who can fix it instead of looping.
    return error instanceof PlannerApiError && error.signingIn
      ? {
          sentence:
            "You are not signed in, or your sign-in has expired. Taking you to the sign-in page.",
          toRuns: false,
        }
      : {
          sentence:
            "The planner refused your sign-in. Contact the administrator to check your access to the planner.",
          toRuns: false,
        };
  }
  if (error instanceof ApiError && error.isForbidden) {
    return { sentence: "You are signed in, but the planner refused this request.", toRuns: false };
  }
  if (error instanceof ApiError) {
    return { sentence: "This could not be read. Reload the page to try again.", toRuns: false };
  }
  // Not an answer at all: fetch itself failed.
  return {
    sentence: "The planner could not be reached. Check the connection, then reload the page.",
    toRuns: false,
  };
}

/** The refusal as one sentence with its way on, for a `Section`'s `error`
 *  (the block draws it as a critical `EmptyState`, read as an alert). */
export function FailureSentence({ error, context }: { error: unknown; context?: FailureContext }) {
  const { sentence, toRuns, signIn } = explain(error, context);
  return (
    <>
      {sentence}
      {signIn ? (
        <>
          {" "}
          <Button onPress={signInAgain} variant="secondary">
            Sign in again
          </Button>
        </>
      ) : null}
      {toRuns ? (
        <>
          {" "}
          <Link to="/runs">All runs</Link>
        </>
      ) : null}
    </>
  );
}

/** A refusal where the screen's content would be: one critical `EmptyState`. */
export function Failure({ error, context }: { error: unknown; context?: FailureContext }) {
  return (
    <EmptyState tone="critical">
      <FailureSentence context={context} error={error} />
    </EmptyState>
  );
}

/** A reading in flight, in one sentence where the content will be, announced
 *  politely rather than shown as a spinner: a spinner says nothing to a
 *  screen reader (WCAG 4.1.3). The `EmptyState` block, in a status region. */
export function Pending({ children }: { children: ReactNode }) {
  return (
    <div role="status">
      <EmptyState>{children}</EmptyState>
    </div>
  );
}
